import React, { useEffect, useRef } from "react";
import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { Close } from "@mui/icons-material";
import PhotoGrid from "./PhotoGrid";
import { radius, tokens } from "../theme/theme";

/**
 * Previews of the photos picked for upload, before they are saved.
 *
 * These files exist only in the browser — there is no URL to fetch yet — so the
 * preview comes from `URL.createObjectURL`. Listing filenames alone told a
 * realtor nothing about whether they had picked the right shots, or picked the
 * same one twice.
 *
 * Pass `onReorder` and the previews become a PhotoGrid — drag, ‹ ›, ★ — which
 * calls it with the files in their new order. Without it (the Edit dialog,
 * where new files join the end of an album that already has an order) they
 * stay a plain grid.
 */
export default function SelectedPhotos({ files, onRemove, onReorder }) {
  const previews = usePreviews(files);

  if (files.length === 0) return null;

  if (onReorder) {
    return (
      <Box sx={{ mt: 2 }}>
        <Typography
          variant="caption"
          sx={{ display: "block", mb: 1.5, color: tokens.ink500 }}
        >
          Вибрано файлів: {files.length}
          {files.length > 1 &&
            ". Перетягніть фото або скористайтесь ‹ ›, щоб змінити порядок. " +
              "Перше фото — головне: воно показується на картці й у посиланнях."}
        </Typography>

        <PhotoGrid
          photos={files.map(function (file, index) {
            return {
              key: previews[index].key,
              name: file.name,
              url: previews[index].url,
              file: file,
            };
          })}
          onReorder={function (next) {
            onReorder(
              next.map(function (photo) {
                return photo.file;
              })
            );
          }}
          actions={[
            {
              key: "remove",
              label: "Прибрати зі списку",
              icon: function () {
                return <Close sx={{ fontSize: 17 }} />;
              },
              onClick: function (photo) {
                onRemove(photo.file);
              },
              sx: removeButtonColors,
            },
          ]}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 2 }}>
      <Typography
        variant="caption"
        sx={{ display: "block", mb: 1, color: tokens.ink500 }}
      >
        Вибрано файлів: {files.length}
      </Typography>

      <Box
        sx={{
          display: "grid",
          // minmax(0, 1fr) rather than 1fr: a long phone-camera filename has a
          // min-content width of its whole string, and a bare 1fr track grows
          // to fit it — one photo then renders four times the size of the rest.
          gridTemplateColumns: {
            xs: "repeat(3, minmax(0, 1fr))",
            sm: "repeat(5, minmax(0, 1fr))",
            md: "repeat(6, minmax(0, 1fr))",
          },
          gap: 1.25,
        }}
      >
        {files.map(function (file, index) {
          return (
            <Box key={previews[index].key} sx={{ minWidth: 0 }}>
              <Box
                sx={{
                  position: "relative",
                  aspectRatio: "1 / 1",
                  borderRadius: radius.md,
                  overflow: "hidden",
                  border: "1px solid " + tokens.line,
                  backgroundColor: tokens.line2,
                  "&:hover .remove-photo": { opacity: 1 },
                  "@media (hover: none)": {
                    "& .remove-photo": { opacity: 1 },
                  },
                }}
              >
                <Box
                  component="img"
                  src={previews[index].url}
                  alt={file.name}
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                <Tooltip title="Прибрати зі списку">
                  <IconButton
                    className="remove-photo"
                    size="small"
                    onClick={function () {
                      onRemove(file);
                    }}
                    sx={{
                      position: "absolute",
                      top: 3,
                      right: 3,
                      opacity: 0,
                      transition: "opacity .15s ease",
                      ...removeButtonColors,
                    }}
                  >
                    <Close sx={{ fontSize: 15 }} />
                  </IconButton>
                </Tooltip>
              </Box>

              <Tooltip title={file.name}>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.5,
                    color: tokens.ink400,
                    // Long phone-camera filenames must not widen the grid cell.
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>
              </Tooltip>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

/**
 * One object URL and one stable React key per selected file.
 *
 * Object URLs pin the file in memory until revoked. Each file keeps its URL for
 * as long as it stays selected — re-creating them all on every reorder made
 * each preview blink while the browser decoded the photo again — and it is
 * revoked once the file is removed, or when the dialog closes.
 */
function usePreviews(files) {
  const cache = useRef(null);
  if (cache.current === null) cache.current = { byFile: new Map(), nextKey: 0 };
  const byFile = cache.current.byFile;

  const previews = files.map(function (file) {
    let preview = byFile.get(file);
    if (!preview) {
      preview = {
        key: "selected-" + cache.current.nextKey++,
        url: URL.createObjectURL(file),
      };
      byFile.set(file, preview);
    }
    return preview;
  });

  useEffect(
    function () {
      byFile.forEach(function (preview, file) {
        if (files.indexOf(file) < 0) {
          URL.revokeObjectURL(preview.url);
          byFile.delete(file);
        }
      });
    },
    [files, byFile]
  );

  useEffect(
    function () {
      return function () {
        byFile.forEach(function (preview) {
          URL.revokeObjectURL(preview.url);
        });
        byFile.clear();
      };
    },
    [byFile]
  );

  return previews;
}

const removeButtonColors = {
  backgroundColor: "rgba(255,255,255,0.92)",
  color: tokens.ink700,
  "&:hover": {
    backgroundColor: tokens.red600,
    color: tokens.surface,
  },
};
